<script setup lang="ts">
import { computed, defineAsyncComponent, watch } from 'vue'
import { useRoute } from 'vue-router'
import RouteErrorBoundary from './RouteErrorBoundary.vue'
import { dismissBootScreen } from './boot-screen'
import { layoutFor, loadedLayout } from './layouts'
import AppProviders from './providers/AppProviders.vue'

const route = useRoute()

/**
 * The page's scrollbar (Naive's, over the page: `ApmpPageScrollbar`). Its own chunk, fetched
 * after the first page is up -- the entry carries none of it.
 */
const PageScrollbar = defineAsyncComponent(
  () => import('~/shared/components/ApmpPageScrollbar.vue'),
)

/**
 * The layout drives BOTH the chrome and the theme, so the two design languages can never
 * appear on one screen (CLAUDE.md rule 6).
 *
 * Layouts are lazy chunks the router loads before a navigation commits (`./layouts.ts`), so by
 * the time a route is current its layout is in memory. The one moment it is not is before the
 * first navigation has finished; `index.html`'s boot screen covers that, and nothing renders
 * under it.
 *
 * Phase 13: the dashboard's sidebar menu (and its ~33 icons) used to be built HERE, which put
 * it in the entry chunk too. The dashboard layout now builds its own menu, so the menu module
 * travels with the layout's lazy chunk and a visitor never downloads it.
 */
const layout = computed(() => loadedLayout(layoutFor(route.meta.layout)))

// `post`, so the first page is in the DOM before the boot screen over it is taken away.
watch(
  layout,
  (current) => {
    if (current) dismissBootScreen()
  },
  { flush: 'post', immediate: true },
)
</script>

<template>
  <AppProviders>
    <PageScrollbar />
    <component :is="layout" v-if="layout" v-bind="route.meta.layoutProps ?? {}">
      <!-- One broken screen must never blank the app: the boundary renders a recoverable
           state in place of the page, and the shell around it keeps working (phase-13 §6). -->
      <!--
        The key decides what counts as "a different page".

        `route.path` by default: navigating from one screen to another remounts and plays the
        page transition, which is the point of it. But a route whose parameter selects a **view
        inside** the page — analytics' `:section?` — changes the path without changing the page,
        and keying on it made switching tabs remount the whole screen and slide it in like a
        navigation. Those routes set `meta.viewParam`, and are keyed by name instead: the tab
        strip animates its own panes, and the page it lives on stays put.
      -->
      <RouterView v-slot="{ Component, route: pageRoute }">
        <Transition :name="pageRoute.meta.layout === 'dashboard' ? 'route' : 'route-none'" mode="out-in">
          <div
            :key="pageRoute.meta.viewParam ? String(pageRoute.name) : pageRoute.path"
            :class="{ 'apmp-route-page': pageRoute.meta.layout === 'dashboard' }"
          >
            <RouteErrorBoundary><component :is="Component" /></RouteErrorBoundary>
          </div>
        </Transition>
      </RouterView>
    </component>
  </AppProviders>
</template>
